import { useCallback, useState } from 'react'
import type { CalendarLayerId } from './layers'

const STORAGE_KEY = 'property-calendar:hidden-layers:v1'

/** The portfolio calendar's own choice (CAP-1417): switching a layer off there does not switch it off on a property's tab. */
export const PORTFOLIO_LAYER_PREFS_KEY = 'portfolio-calendar:hidden-layers:v1'

/**
 * Which layers the viewer has switched off. Stores the hidden ones, not the
 * visible ones, so a layer added later is on for everybody until they turn it
 * off.
 *
 * A per-viewer convenience and nothing more: it never reaches the API and no
 * decision depends on it. Storage can be absent, full or blocked (private
 * windows, site data cleared), so every read and write is guarded and the
 * calendar renders the same without it - everything on.
 */
function readHidden(key: string): Set<string> {
  try {
    const raw = window.localStorage.getItem(key)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return new Set(Array.isArray(parsed) ? parsed.filter(v => typeof v === 'string') : [])
  } catch {
    return new Set()
  }
}

function writeHidden(key: string, hidden: Set<string>) {
  try {
    window.localStorage.setItem(key, JSON.stringify([...hidden]))
  } catch {
    // Not persisted; the choice still holds for this visit.
  }
}

export function useLayerPrefs(storageKey: string = STORAGE_KEY) {
  const [hidden, setHidden] = useState<Set<string>>(() => readHidden(storageKey))

  const isVisible = useCallback((id: CalendarLayerId) => !hidden.has(id), [hidden])

  const toggle = useCallback(
    (id: CalendarLayerId) => {
      setHidden(prev => {
        const next = new Set(prev)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        writeHidden(storageKey, next)
        return next
      })
    },
    [storageKey]
  )

  return { isVisible, toggle }
}
